<script setup lang="ts">
import { computed } from 'vue'
import { clamp } from 'lodash'
import { formatCurrency } from '@/modules/shared/utils/currency'
import {
  VCard,
  VCardTitle,
  VDescriptionListDetailed,
  VMetricCard,
  VOperatingActivity,
} from '@/modules/shared/components'
import type { DescriptionListDetailedItem, OperatingActivityItem } from '@/modules/shared/components/types'
import { ArrowUpRight, Diamond, Equal, Repeat, RotateCcw, Star } from 'lucide-vue-next'
import SummaryCardSkeleton from './summary-card-skeleton.vue'

const props = withDefaults(
  defineProps<{
    distributedPaid: number
    distributedPending: number
    returnOfCapital: number
    preferredReturn: number
    profit: number
    carriedInterest: number
    netDistributed: number
    otherFees: number
    recycledFunds: number
    hurdle?: number
    currency?: string
    skeleton?: boolean
  }>(),
  { hurdle: 0, currency: 'USD', skeleton: false },
)

const fmt = (value: number) => formatCurrency(value, { currency: props.currency, decimals: 0 })

const metricValue = computed(() => fmt(props.distributedPaid + props.distributedPending))

const paidBar = computed(() => {
  if (props.distributedPaid <= 0) return undefined
  const width = props.hurdle > 0 ? clamp((props.distributedPaid / props.hurdle) * 100, 0, 100) : 0
  return { value: fmt(props.distributedPaid), width, color: '#408F93', gradientTo: '#246586' }
})

const pendingBar = computed(() => {
  if (props.distributedPending <= 0) return undefined
  const width =
    props.hurdle > 0 ? clamp(((props.distributedPaid + props.distributedPending) / props.hurdle) * 100, 0, 100) : 0
  return { value: fmt(props.distributedPending), width, color: '#7dc9cc' }
})

const rows = computed<DescriptionListDetailedItem[]>(() => [
  {
    icon: RotateCcw,
    label: 'Return of Capital',
    sublabel: 'capital returned to investors',
    value: fmt(props.returnOfCapital),
    color: '#408F93',
  },
  {
    icon: Star,
    label: 'Preferred Return',
    sublabel: 'priority return to investors',
    value: fmt(props.preferredReturn),
    color: '#4FB8A9',
  },
  {
    icon: ArrowUpRight,
    label: 'Profit',
    sublabel: 'gains above committed capital',
    value: fmt(props.profit),
    color: '#46B37D',
  },
  {
    icon: Diamond,
    label: 'Carried Interest',
    sublabel: 'GP profit share',
    value: fmt(props.carriedInterest),
    color: '#7d8ad0',
  },
  {
    icon: Equal,
    label: 'Net Distributed',
    sublabel: 'distributed to investors',
    value: fmt(props.netDistributed),
    color: '#246586',
  },
])

const otherItems = computed<OperatingActivityItem[]>(() => [
  { label: 'Other Fees', value: fmt(props.otherFees), color: '#7d8ad0' },
  { label: 'Recycled Funds', value: fmt(props.recycledFunds), color: '#246586' },
])
</script>

<template>
  <VCard>
    <VCardTitle title="Distribution Summary" />

    <SummaryCardSkeleton v-if="skeleton" :rows="5" />

    <template v-else>
      <div class="flex items-end justify-between">
        <div>
          <div class="text-3xl font-medium tracking-tight text-[#408F93]">
            {{ metricValue }}
          </div>
          <div class="mt-1 text-sm font-medium text-gray-700">Distributed</div>
        </div>
        <div>
          <div class="text-right text-2xl font-medium tracking-tight text-gray-600">
            {{ fmt(props.hurdle || 0) }}
          </div>
          <div class="mt-1 text-right text-sm font-medium text-gray-700">Hurdle</div>
        </div>
      </div>

      <VMetricCard
        label="Distributed"
        :value="metricValue"
        color="#408F93"
        :paid="paidBar"
        :pending="pendingBar"
        class="mb-4"
      />
      <VDescriptionListDetailed :items="rows" />
      <div class="border-t border-[var(--divider-color,#EEEFF0)]"></div>
      <VOperatingActivity
        :items="otherItems"
        title="Other"
        subtitle="fees and recycled funds"
        :icon="Repeat"
        icon-color="#7d8ad0"
      />
    </template>
  </VCard>
</template>
